/**
 * Settings → Desktop app: download the Docsight Uploader, and what to do
 * once it is installed.
 *
 * The builds are not hardcoded. A version baked into the bundle is wrong the day
 * after the next patch release and would need a redeploy to correct; everything
 * on the cards comes from the record a system admin publishes on
 * Admin → Desktop app, so a new release needs no change here.
 *
 * Folder watching is described below but configured *in the app*: it operates on
 * the local filesystem, which a browser cannot see. The example mapping
 * deliberately carries no controls, so it is never mistaken for a setting that
 * lives on this page.
 */
import { useCallback, useEffect, useState } from 'react';

import type { DesktopBuild, DesktopReleaseView } from '../../api/schema';
import { Button, EmptyState, ErrorState, Skeleton } from '../../components';
import { useLoadState } from '../../lib/useLoadState';
import { MacosIcon, WindowsIcon } from '../../shell/icons';
import { useSettingsApi } from './settingsApi';
import './settings.css';

type Platform = 'windows' | 'macos';

const PLATFORM_ORDER: readonly Platform[] = ['windows', 'macos'];
const PLATFORM_LABEL: Record<Platform, string> = { windows: 'Windows', macos: 'macOS' };
const PLATFORM_EXTENSION: Record<Platform, string> = { windows: '.exe', macos: '.dmg' };

/**
 * The viewer's own platform, or `null` when we cannot tell.
 *
 * Read after mount, never during render — ordering the cards from `navigator`
 * while rendering would differ between the first paint and the next, and "we
 * could not tell" has to stay a real answer rather than a guess at Windows.
 */
function detectPlatform(): Platform | null {
  const agent = navigator.userAgent;
  if (/Mac|iPhone|iPad/i.test(agent)) return 'macos';
  if (/Win/i.test(agent)) return 'windows';
  return null;
}

/**
 * A link we are willing to put in an `href`.
 *
 * The admin form and the backend both refuse anything but https, but this is the
 * last hop before the value reaches an anchor — a `javascript:` URL surviving
 * this far would be an XSS with two earlier checks to blame and nobody catching
 * it.
 */
function safeUrl(url: string | null | undefined): string | null {
  return typeof url === 'string' && /^https:\/\/.+/i.test(url.trim()) ? url.trim() : null;
}

/** "Windows 10 or later · 95 MB" — each half optional, so an incomplete record
 * reads as a shorter line rather than an empty separator. */
function buildMeta(build: DesktopBuild): string {
  const mb = build.size_mb;
  const size = mb === null || mb === undefined || mb <= 0 ? null : `${String(Math.round(mb * 10) / 10)} MB`;
  return [build.min_os, size].filter(Boolean).join(' · ');
}

/** One published build. Every fact on it comes from the release record. */
function DownloadCard({
  platform,
  build,
  version,
  notesUrl,
}: {
  platform: Platform;
  build: DesktopBuild;
  version: string | null;
  notesUrl: string | null;
}): React.JSX.Element | null {
  const href = safeUrl(build.url);
  if (href === null) return null;

  const msi = safeUrl(build.alt_url);
  const notes = safeUrl(notesUrl);
  const meta = buildMeta(build);

  return (
    <div className="set-download">
      <span className="set-download__glyph">
        {platform === 'windows' ? <WindowsIcon size={20} /> : <MacosIcon size={20} />}
      </span>
      <span className="set-download__name">{PLATFORM_LABEL[platform]}</span>
      {meta === '' ? null : <span className="set-hint">{meta}</span>}
      {/* A real file download, not a route: an anchor, so the browser's own
          download handling applies and right-click "save as" works. */}
      <a className="set-download__btn" href={href} download>
        Download {PLATFORM_EXTENSION[platform]}
      </a>
      <span className="set-hint">
        {version === null ? '—' : `v${version}`}
        {msi === null ? null : (
          <>
            {' · '}
            <a href={msi} download>
              MSI for IT deployment
            </a>
          </>
        )}
        {notes === null ? null : (
          <>
            {' · '}
            <a href={notes} target="_blank" rel="noreferrer">
              Release notes
            </a>
          </>
        )}
      </span>
    </div>
  );
}

/** Folder → what Docsight does with it. An illustration, not a control. */
const FOLDER_EXAMPLES: readonly { path: string; source: string }[] = [
  { path: 'Documents\\Invoices', source: 'finance-desktop' },
  { path: 'Documents\\Contracts', source: 'legal-desktop' },
];

function Step({
  index,
  title,
  children,
}: {
  index: number;
  title: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <li className="set-step">
      <span className="set-step__num" aria-hidden="true">
        {index}
      </span>
      <div>
        <h3 className="set-step__title">{title}</h3>
        <div className="set-step__body">{children}</div>
      </div>
    </li>
  );
}

/** How to get from a fresh install to classified documents. */
function SetupSteps(): React.JSX.Element {
  return (
    <ol className="set-steps">
      <Step index={1} title="Create a key for this machine">
        On <strong>Sources &amp; API keys</strong>, create a key with{' '}
        <code className="set-mono">documents:write</code>. Name it after the device or team so you
        can revoke just that one later — &ldquo;Desktop uploader — Finance&rdquo; rather than
        &ldquo;key 2&rdquo;.
      </Step>

      <Step index={2} title="Install and paste the key">
        The app asks for it once on first launch and stores it in the operating system keychain —
        Credential Manager on Windows, Keychain on macOS. It never sits in a plain file.
      </Step>

      <Step index={3} title="Add folders in the app and name each one&rsquo;s source">
        In the app, choose any folder on your machine and give it a source name. Watch as many
        folders as you like; each keeps its own source, so the Documents explorer can tell a
        finance drop from a legal one without anybody tagging files by hand.
        <div className="set-folders">
          {FOLDER_EXAMPLES.map((example) => (
            <div className="set-folder" key={example.path}>
              <span className="set-mono">{example.path}</span>
              <span aria-hidden="true" className="set-folder__arrow">
                →
              </span>
              <span className="set-folder__tag">{example.source}</span>
            </div>
          ))}
        </div>
      </Step>

      <Step index={4} title="Drop files in">
        Anything added to a watched folder is uploaded to{' '}
        <code className="set-mono">POST /v1/documents</code>, OCR&rsquo;d and classified against
        your taxonomy, and appears in Documents within a minute. Re-dropping the same file is
        safe: the app sends an <code className="set-mono">Idempotency-Key</code>, so a retry does
        not create a duplicate.
      </Step>
    </ol>
  );
}

export function DesktopUploaderScreen(): React.JSX.Element {
  const settingsApi = useSettingsApi();
  const fetchRelease = useCallback(
    (signal: AbortSignal): Promise<DesktopReleaseView | null> => settingsApi.desktopRelease(signal),
    [settingsApi],
  );
  const state = useLoadState(fetchRelease);

  // Resolved after mount — see `detectPlatform`.
  const [platform, setPlatform] = useState<Platform | null>(null);
  useEffect(() => {
    setPlatform(detectPlatform());
  }, []);

  if (state.status === 'loading') {
    return (
      <section className="set-wrap" aria-label="Desktop app">
        <Skeleton height={120} />
        <Skeleton height={220} />
      </section>
    );
  }

  if (state.status === 'error') {
    return (
      <section className="set-wrap" aria-label="Desktop app">
        <ErrorState
          title="We couldn't load the download"
          body="The settings service didn't respond. Your ingestion keeps running — this only affects this view."
          traceId={state.traceId ?? undefined}
          actions={
            <Button onClick={state.reload} variant="primary">
              Retry
            </Button>
          }
        />
      </section>
    );
  }

  const release: DesktopReleaseView | null = state.data;
  // Paired with the build up front, so the render never has to re-assert that a
  // platform the list named actually carries one.
  const published = PLATFORM_ORDER.map((name) => ({ name, build: release?.[name] })).filter(
    (entry): entry is { name: Platform; build: DesktopBuild } => entry.build != null,
  );
  // The viewer's own platform first, if we could tell and we ship it.
  const ordered =
    platform !== null && published.some((entry) => entry.name === platform)
      ? [
          ...published.filter((entry) => entry.name === platform),
          ...published.filter((entry) => entry.name !== platform),
        ]
      : published;

  return (
    <section className="set-wrap set-wrap--fit" aria-label="Desktop app">
      <div className="set-card">
        <h2 className="set-card__title">Docsight Uploader</h2>
        <p className="set-hint">
          A small desktop app for people who need to send documents in regularly but don&rsquo;t
          write code. Point it at folders on your machine and anything dropped in is uploaded,
          OCR&rsquo;d and classified automatically.
        </p>

        {ordered.length === 0 || release === null ? (
          /* No build published, unreachable, or a record with no installer lands
             here rather than failing the screen — the setup guidance below is
             still worth reading. */
          <EmptyState
            title="No build available yet"
            body="Downloads appear here as soon as your provider publishes one. The setup steps below already apply."
          />
        ) : (
          <>
            <div className="set-downloads">
              {ordered.map((entry) => (
                <DownloadCard
                  key={entry.name}
                  platform={entry.name}
                  build={entry.build}
                  version={release.version ?? null}
                  notesUrl={release.notes_url ?? null}
                />
              ))}
            </div>
            {platform !== null && ordered.length > 1 ? (
              <p className="set-note">
                Your platform is shown first. The other build is available beside it.
              </p>
            ) : null}
          </>
        )}
      </div>

      {/* The guide is the long part, so it takes the leftover height and scrolls
          inside itself — the downloads stay put above it rather than being
          pushed off the top of a scrolling page. */}
      <div className="set-card set-card--fill">
        <h2 className="set-card__title">Setting it up</h2>
        <div className="set-scrollpane">
          <SetupSteps />
        </div>
      </div>
    </section>
  );
}
